import {
    Card,
    Checkbox,
    InlineStack,
    Layout,
    Page,
    PageActions,
    SkeletonBodyText,
    SkeletonPage,
    Box,
    Tabs,
    Spinner,
    Text,
    IndexTable,
    TextField,
} from "@shopify/polaris";
import { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { UserSetting } from "../../app/models/UserSetting";
import { useStore } from "../../app/stores/store";

const SettingsPage: React.FC = () => {
    const { settingStore } = useStore();
    const {
        currentSettings: settings,
        loadingUpdate,
        loadingInitial,
        loadSettings,
        inventoryLocations,
        loadingInventoryLocations,
        setInventoryLocationSelected,
        selectedInventoryLocationIds,
        loadInventoryLocations,
        vendorSkuRules,
        loadingVendorSkuRules,
        loadVendorSkuRules,
        setVendorSkuPrefix,
        setVendorSkuNormalize,
    } = settingStore;

    const [settingsLoaded, setSettingsLoaded] = useState(false);
    const [updateInProgress, setUpdateInProgress] = useState(false);
    const [persistentFilteringChecked, setPersistentFilteringChecked] = useState(false);
    const [inventoryAutoMapEnabled, setInventoryAutoMapEnabled] = useState(false);
    const [taggingTagPrefixEnabled, setTaggingTagPrefixEnabled] = useState(true);

    // Tabs state
    const [selectedTab, setSelectedTab] = useState(0);
    const handleTabChange = useCallback((index: number) => setSelectedTab(index), []);

    const tabs = [
        { id: "general-tab", content: "General", accessibilityLabel: "General settings", panelID: "general-panel" },
        { id: "vendor-sku-rules-tab", content: "Vendor SKU Rules", accessibilityLabel: "Vendor SKU Rules settings", panelID: "vendor-sku-rules-panel" },
        { id: "inventory-tab", content: "Inventory", accessibilityLabel: "Inventory settings", panelID: "inventory-panel" },
    ];

    // Load settings once
    useEffect(() => {
        const fetchSettings = async () => {
            await loadSettings();
            setSettingsLoaded(true);
        };
        if (!settingsLoaded) fetchSettings();
    }, [settingsLoaded, loadSettings]);

    // Seed local state when settings identity changes
    useEffect(() => {
        if (settings) {
            setPersistentFilteringChecked(!!settings.searchEnablePersistentFiltering);
            setInventoryAutoMapEnabled(!!settings.inventoryAutoMapEnabled);
            setTaggingTagPrefixEnabled(settings.useYmmPrefixForProductTags ?? true);
        }
    }, [settings?.id]);

    // Load inventory locations once
    useEffect(() => {
        loadInventoryLocations();
    }, [loadInventoryLocations]);

    // Load vendor SKU rules once
    useEffect(() => {
        loadVendorSkuRules();
    }, [loadVendorSkuRules]);

    const handlePersistentFilteringChange = useCallback(
        (newChecked: boolean) => setPersistentFilteringChecked(newChecked),
        []
    );

    const handleInventoryAutoMapChange = useCallback((newChecked: boolean) => {
        setInventoryAutoMapEnabled(newChecked);
    }, []);

    const handleTaggingTagPrefixChange = useCallback((newChecked: boolean) => {
        setTaggingTagPrefixEnabled(newChecked);
    }, []);

    const handleInventoryLocationToggle = useCallback(
        (id: number, checked: boolean) => {
            if (!inventoryAutoMapEnabled) return;
            setInventoryLocationSelected(id, checked);
        },
        [inventoryAutoMapEnabled, setInventoryLocationSelected]
    );

    const rows = useMemo(
        () =>
            inventoryLocations.map((loc, index) => {
                const statusBits: string[] = [];
                if (loc.isPrimary) statusBits.push("Primary");
                if (!loc.isActive) statusBits.push("Inactive");
                const status = statusBits.join(" · ") || "—";

                return (
                    <IndexTable.Row id={String(loc.id)} key={loc.id} position={index}>
                        {/* Our ONLY checkbox (IndexTable selection is disabled) */}
                        <IndexTable.Cell>
                            <Checkbox
                                label=""
                                checked={!!loc.selected}
                                onChange={(newChecked) => handleInventoryLocationToggle(loc.id, newChecked)}
                                disabled={!inventoryAutoMapEnabled}
                            />
                        </IndexTable.Cell>

                        <IndexTable.Cell>
                            <Text as="span" variant="bodyMd" fontWeight="bold">
                                {loc.name}
                            </Text>
                        </IndexTable.Cell>

                        <IndexTable.Cell>
                            <Text as="span" variant="bodySm" tone="subdued">
                                {loc.address || "—"}
                            </Text>
                        </IndexTable.Cell>

                        <IndexTable.Cell>
                            <Text as="span" variant="bodySm" tone="subdued">
                                {status}
                            </Text>
                        </IndexTable.Cell>
                    </IndexTable.Row>
                );
            }),
        [inventoryLocations, handleInventoryLocationToggle, inventoryAutoMapEnabled]
    );

    const vendorRows = useMemo(
        () =>
            vendorSkuRules.map((rule, index) => (
                <IndexTable.Row id={`${rule.shopifyVendor}-${index}`} key={rule.shopifyVendor} position={index}>
                    <IndexTable.Cell>
                        <Text as="span" variant="bodyMd" fontWeight="bold">
                            {rule.shopifyVendor}
                        </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                        <TextField
                            label="SKU Prefix"
                            labelHidden
                            autoComplete="off"
                            value={rule.skuPrefix || ""}
                            onChange={(value) => setVendorSkuPrefix(rule.shopifyVendor, value)}
                            placeholder="Optional prefix"
                        />
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                        <Checkbox
                            label=""
                            checked={!!rule.normalizeSku}
                            onChange={(value) => setVendorSkuNormalize(rule.shopifyVendor, value)}
                        />
                    </IndexTable.Cell>
                </IndexTable.Row>
            )),
        [vendorSkuRules, setVendorSkuPrefix, setVendorSkuNormalize]
    );

    const handleSaveSettings = async () => {
        const newSetting: UserSetting = {
            searchEnablePersistentFiltering: persistentFilteringChecked,
            inventoryAutoMapEnabled: inventoryAutoMapEnabled,
            useYmmPrefixForProductTags: taggingTagPrefixEnabled,
            inventoryLocationIds: selectedInventoryLocationIds,
        };

        if (settings?.id !== undefined) {
            newSetting.id = settings.id;
        }
        setUpdateInProgress(true);
        try {
            await Promise.all([
                settingStore.updateSettings(newSetting),
                settingStore.updateVendorSkuRules(vendorSkuRules),
            ]);
        } finally {
            setUpdateInProgress(false);
        }
    };

    if (!settingsLoaded || loadingInitial) {
        return (
            <SkeletonPage title="Settings">
                <Layout>
                    <Layout.Section>
                        <Card>
                            <Box padding="400">
                                <SkeletonBodyText />
                            </Box>
                        </Card>
                    </Layout.Section>
                </Layout>
            </SkeletonPage>
        );
    }

    return (
        <Page title="Settings">
            <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange}>
                {/* General */}
                {selectedTab === 0 && (
                    <Layout>
                        <Layout.Section>
                            <Card>
                                <Box padding="400">
                                    <Checkbox
                                        label="Enable persistent filtering on vehicle search"
                                        checked={!!persistentFilteringChecked}
                                        onChange={(newChecked) => handlePersistentFilteringChange(newChecked)}
                                    />
                                    <Box paddingBlockStart="400">
                                        <Checkbox
                                            label="Use ymm_ prefix for product tags"
                                            checked={taggingTagPrefixEnabled}
                                            onChange={(newChecked) => handleTaggingTagPrefixChange(newChecked)}
                                        />
                                        <Box paddingBlockStart="100">
                                            <Text as="p" variant="bodySm" tone="subdued">
                                                Recommended: keep this enabled. Disabling the prefix can create tag collisions and may cause tagging/sync issues.
                                            </Text>
                                        </Box>
                                    </Box>
                                </Box>
                            </Card>
                        </Layout.Section>
                    </Layout>
                )}

                {/* Inventory */}
                {selectedTab === 2 && (
                    <Layout>
                        <Layout.Section>
                            <Card>
                                {!loadingInventoryLocations && inventoryLocations.length > 0 && (
                                    <Box padding="400" paddingBlockEnd="200">
                                        <InlineStack gap="200" align="start" blockAlign="center">
                                            <Checkbox
                                                label="Ensure selected locations are mapped to every product"
                                                checked={inventoryAutoMapEnabled}
                                                onChange={(newChecked) => handleInventoryAutoMapChange(newChecked)}
                                            />
                                        </InlineStack>

                                        <Box paddingBlockStart="200">
                                            <Text as="p" variant="bodyMd">
                                                The selected inventory locations will be added to any product that is missing them so every product has these locations mapped.
                                            </Text>
                                            <Box paddingBlockStart="100">
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    Existing product-location mappings will remain untouched—this only supplements missing locations.
                                                </Text>
                                            </Box>
                                        </Box>

                                        {!inventoryAutoMapEnabled && (
                                            <Box paddingBlockStart="200">
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    Enable automatic mapping to configure default locations.
                                                </Text>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                <Box padding="0">
                                    {loadingInventoryLocations ? (
                                        <Box padding="400">
                                            <InlineStack gap="200" align="center">
                                                <Spinner accessibilityLabel="Loading inventory locations" size="small" />
                                                <Text as="span" variant="bodyMd">
                                                    Loading inventory locations…
                                                </Text>
                                            </InlineStack>
                                        </Box>
                                    ) : inventoryLocations.length === 0 ? (
                                        <Box padding="400">
                                            <Text as="p" variant="bodyMd">No inventory locations are currently available for this shop.</Text>
                                        </Box>
                                    ) : (
                                        <IndexTable
                                            resourceName={{ singular: "location", plural: "locations" }}
                                            itemCount={inventoryLocations.length}
                                            selectable={false} // <-- disable built-in selection column
                                            headings={[
                                                { title: "Selected" },
                                                { title: "Location" },
                                                { title: "Address" },
                                                { title: "Status" },
                                            ]}
                                        >
                                            {rows}
                                        </IndexTable>
                                    )}
                                </Box>
                            </Card>
                        </Layout.Section>
                    </Layout>
                )}

                {/* Vendor SKU Rules */}
                {selectedTab === 1 && (
                    <Layout>
                        <Layout.Section>
                            <Card>
                                <Box padding="400" paddingBlockEnd="200">
                                    <Text as="p" variant="bodyMd">
                                        These vendor SKU rules control how imported part numbers are mapped to Shopify SKUs for each vendor.
                                        Use SKU Prefix to append a suffix-style mapping value and enable Normalize SKU to remove non-alphanumeric characters before matching.
                                    </Text>
                                </Box>

                                <Box padding="0">
                                    {loadingVendorSkuRules ? (
                                        <Box padding="400">
                                            <InlineStack gap="200" align="center">
                                                <Spinner accessibilityLabel="Loading vendor SKU rules" size="small" />
                                                <Text as="span" variant="bodyMd">
                                                    Loading vendor SKU rules…
                                                </Text>
                                            </InlineStack>
                                        </Box>
                                    ) : vendorSkuRules.length === 0 ? (
                                        <Box padding="400">
                                            <Text as="p" variant="bodyMd">No vendors found for this shop yet.</Text>
                                        </Box>
                                    ) : (
                                        <IndexTable
                                            resourceName={{ singular: "vendor", plural: "vendors" }}
                                            itemCount={vendorSkuRules.length}
                                            selectable={false}
                                            headings={[
                                                { title: "Vendor" },
                                                { title: "SKU Prefix" },
                                                { title: "Normalize SKU" },
                                            ]}
                                        >
                                            {vendorRows}
                                        </IndexTable>
                                    )}
                                </Box>
                            </Card>
                        </Layout.Section>
                    </Layout>
                )}
            </Tabs>

            <PageActions
                primaryAction={{
                    content: "Save",
                    loading: updateInProgress || loadingUpdate,
                    onAction: handleSaveSettings,
                }}
            />
        </Page>
    );
};

export default observer(SettingsPage);
